/** 课堂记录 / 考勤、两周家长反馈、行规分批量点评、课程评语 AI 起草(博颂 2026-09-24)。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authHeaders } from "./school";

/** 带 HTTP 状态码的错误(课堂记录用 409 表示点名册已被别人保存过)。 */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    headers: { ...authHeaders(), ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => ({}))) as { detail?: unknown };
    throw new ApiError(typeof b.detail === "string" ? b.detail : `HTTP ${r.status}`, r.status);
  }
  return r.status === 204 ? (undefined as T) : ((await r.json()) as T);
}

// ── 课堂记录 / 考勤 ──────────────────────────────────────────────────────

export type Attendance = "present" | "late" | "absent" | "leave" | "early";
export const ATTENDANCE: Attendance[] = ["present", "late", "absent", "leave", "early"];

export interface SheetRow {
  student_id: string;
  name_zh: string;
  name_en: string | null;
  student_code: string | null;
  attendance: Attendance;
  performance: number | null;
  note: string | null;
  /** 班主任登记的请假(054):这一天有登记就带上,点名册默认「请假」 */
  registered_leave?: RegisteredLeave | null;
  /** 同日同节已在别的点名册(分层课 / 整班)里点过 —— 博颂 2026-10-13 重复点名 */
  elsewhere?: Elsewhere | null;
}
export interface Elsewhere { lesson_id: string; label: string; teacher_name: string | null; whole_class: boolean }
export interface RegisteredLeave { id: string; kind: LeaveKind; label: string; note: string | null; start_date: string; end_date: string; by: string | null }
export type LeaveKind = "personal" | "sick" | "other";
export interface LeaveRow { id: string; student_id: string; name_zh: string; name_en: string | null; start_date: string; end_date: string; kind: LeaveKind; label: string; note: string | null; by: string | null; created_at: string }
export interface LessonSheet {
  class_id: string;
  date: string;
  period: number;
  saved: boolean;
  topic: string | null;
  grade_item?: string | null;
  subject?: string | null;
  subject_name?: string | null;
  teacher_name: string | null;
  updated_at: string | null;
  /** 课程(分层课)点名册:重叠只提示;行政班整班点名册:重叠的学生默认不点 */
  is_course?: boolean;
  students: SheetRow[];
}
export interface LessonSummary {
  id: string;
  date: string;
  period: number;
  topic: string | null;
  teacher_name: string | null;
  /** 分层课/课程上的点名 → 那门课的 id;行政班自己的点名为 null */
  course_id: string | null;
  subject: string | null;
  subject_name: string | null;
  students: number;
  late: number;
  absent: number;
  leave: number;
  early: number;
  notes: number;
  avg_performance: number | null;
}

export function useLessonSheet(classId: string, date: string, period: number) {
  return useQuery({
    queryKey: ["lesson-sheet", classId, date, period],
    queryFn: () => req<LessonSheet>("GET", `/api/school/lessons/classes/${classId}/sheet?date=${date}&period=${period}`),
    enabled: !!classId && !!date,
  });
}

/** 课堂记录的学科选项:行政班当前学期的课程;partial = 分层课(名单是子集),点名记在课程上。 */
export interface LessonSubject { slug: string; subject_id?: string; name: string; course_id: string; course_name: string; students: number; partial: boolean }
export function useLessonSubjects(classId: string) {
  return useQuery({
    queryKey: ["lesson-subjects", classId],
    queryFn: () => req<LessonSubject[]>("GET", `/api/school/lessons/classes/${classId}/subjects`),
    enabled: !!classId,
    staleTime: 60_000,
  });
}

/** 班主任请假登记(054):本班前 7 天到后 60 天。 */
export function useLeaves(homeroomId: string) {
  return useQuery({
    queryKey: ["leaves", homeroomId],
    queryFn: () => req<LeaveRow[]>("GET", `/api/school/lessons/classes/${homeroomId}/leaves`),
    enabled: !!homeroomId,
  });
}
export function useLeaveMutations(homeroomId: string) {
  const qc = useQueryClient();
  const inv = () => { void qc.invalidateQueries({ queryKey: ["leaves", homeroomId] }); void qc.invalidateQueries({ queryKey: ["lesson-sheet"] }); };
  return {
    add: useMutation({ mutationFn: (p: { student_id: string; start_date: string; end_date: string; kind: LeaveKind; note: string | null }) =>
      req<{ id: string; converted_absences: number }>("POST", `/api/school/lessons/classes/${homeroomId}/leaves`, p), onSuccess: inv }),
    remove: useMutation({ mutationFn: (id: string) => req<void>("DELETE", `/api/school/lessons/leaves/${id}`), onSuccess: inv }),
  };
}

// 行政班的点名记录(含它所有课程/分层课的);date 给了只列那一天
export function useRecentLessons(classId: string, date: string | null) {
  return useQuery({
    queryKey: ["lessons-recent", classId, date],
    queryFn: () => req<LessonSummary[]>("GET", `/api/school/lessons/classes/${classId}/recent${date ? `?date=${date}` : ""}`),
    enabled: !!classId,
  });
}

export function useSaveLessonSheet(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    // expected_updated_at:读到的点名册 updated_at(新点名册为 null);期间有人保存过 → 409
    mutationFn: (p: { date: string; period: number; topic: string | null; grade_item?: string | null; subject?: string | null; expected_updated_at: string | null; excluded?: string[]; marks: Pick<SheetRow, "student_id" | "attendance" | "performance" | "note">[] }) =>
      req<{ saved: number; conduct_changes: number; updated_at: string; teacher_name: string | null }>("PUT", `/api/school/lessons/classes/${classId}/sheet`, p),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lesson-sheet", classId] });
      void qc.invalidateQueries({ queryKey: ["lessons-recent"] });
      void qc.invalidateQueries({ queryKey: ["conduct-class"] });
      void qc.invalidateQueries({ queryKey: ["conduct-student"] });
    },
  });
}

export function useDeleteLesson(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => req<void>("DELETE", `/api/school/lessons/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lesson-sheet", classId] });
      void qc.invalidateQueries({ queryKey: ["lessons-recent"] });
      void qc.invalidateQueries({ queryKey: ["conduct-class"] });
    },
  });
}

export function useAttendanceRules() {
  return useQuery({ queryKey: ["attendance-rules"], queryFn: () => req<Record<string, number>>("GET", "/api/school/lessons/attendance-rules") });
}

export function useSaveAttendanceRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: Record<string, number>) => req<Record<string, number>>("PUT", "/api/school/lessons/attendance-rules", r),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["attendance-rules"] }),
  });
}

// ── 行规分批量点评 ─────────────────────────────────────────────────────

export function useConductBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { class_id: string; student_ids: string[]; category: string; item: string | null; points: number; note: string | null; recorded_on: string | null }) =>
      req<{ created: number }>("POST", "/api/school/gpa/conduct/records/batch", p),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["conduct-class"] });
      void qc.invalidateQueries({ queryKey: ["conduct-student"] });
      void qc.invalidateQueries({ queryKey: ["gpa-transcript"] });
    },
  });
}

// ── 课程评语 AI 起草 ────────────────────────────────────────────────────

export const draftCourseComment = (classId: string, studentId: string, hint: string | null) =>
  req<{ student_id: string; comment: string }>("POST", `/api/school/gpa/classes/${classId}/comments/draft/${studentId}`, { hint });

// ── 两周家长反馈 ────────────────────────────────────────────────────────

export interface FeedbackRow {
  student_id: string;
  name_zh: string;
  name_en: string | null;
  student_code: string | null;
  guardians: number;
  last_published_end: string | null;
  feedback: {
    id: string;
    period_start: string;
    content: string;
    edited: boolean;
    status: "draft" | "published";
    published_at: string | null;
    updated_at: string;
    teacher_name: string | null;
  } | null;
}
export interface ClassFeedback {
  class_id: string;
  period_end: string;
  students: FeedbackRow[];
}

export function useClassFeedback(classId: string, periodEnd: string) {
  return useQuery({
    queryKey: ["class-feedback", classId, periodEnd],
    queryFn: () => req<ClassFeedback>("GET", `/api/school/feedback/classes/${classId}?period_end=${periodEnd}`),
    enabled: !!classId,
  });
}

export const generateFeedback = (classId: string, studentId: string, p: { period_end: string; days: number; hint: string | null }) =>
  req<{ status: "draft" | "no_data"; content?: string }>("POST", `/api/school/feedback/classes/${classId}/students/${studentId}/generate`, p);

export function useFeedbackMutations(classId: string) {
  const qc = useQueryClient();
  const inv = () => void qc.invalidateQueries({ queryKey: ["class-feedback", classId] });
  return {
    save: useMutation({ mutationFn: (p: { id: string; content: string }) => req("PUT", `/api/school/feedback/${p.id}`, { content: p.content }), onSuccess: inv }),
    publish: useMutation({ mutationFn: (id: string) => req<{ ok: boolean; notified?: number }>("POST", `/api/school/feedback/${id}/publish`), onSuccess: inv }),
    unpublish: useMutation({ mutationFn: (id: string) => req("POST", `/api/school/feedback/${id}/unpublish`), onSuccess: inv }),
    invalidate: inv,
  };
}

/** 以固定并发跑一组异步任务,逐个回报进度。 */
export async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (i < items.length) {
      const item = items[i++] as T;
      await fn(item);
    }
  });
  await Promise.all(workers);
}
